import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { LabelBox, Button } from "../../components";
import { colors } from "../../theme/colors";
import { useGetSpecialitiesQuery, useUpdateSpecialityDetailsMutation } from "../../services/api";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import style from "./speciality.module.css";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";
import { ConfirmationDialog, DetailsDialog, InputDialog } from "../../components/Dialog";
import { updateStatusSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof updateStatusSchema.__outputType;
type SpecialityAction =
  | "view"
  | "edit"
  | "logs"
  | "deactivate"
  | "activate";

/**
 * Specialities listing page with search, table actions, and status updates.
 *
 * Notes:
 * - Relies on RTK Query hooks for list data and status mutations.
 * - Uses react-hook-form to validate deactivation reason input.
 */
const Specialities = () => {
  const [updateSpecialityDetails] = useUpdateSpecialityDetailsMutation();
  const navigate = useNavigate();
  const buildDetailsPath = (id: number | undefined) =>
    `/specialities/details/${id}`;
  const buildEditPath = (id: number | undefined) => `/specialities/edit/${id}`;

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });
  const { data: specialityData, isFetching } = useGetSpecialitiesQuery(queryParam);

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSpeciality, setSelectedSpeciality] = useState<Speciality | null>(
    null
  );
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles action menu selections for a speciality row.
   *
   * Args:
   *   value (SpecialityAction): Action selected from the row menu.
   *   item (Speciality): Speciality associated with the action.
   *
   * Returns:
   *   void
   */
  const handleMenu = useCallback(
    (value: SpecialityAction, item: Speciality) => {
      setSelectedSpeciality(item);
      switch (value) {
        case "deactivate":
        case "activate":
          setModalVisible(true);
          break;
        case "view":
          navigate(buildDetailsPath(item?.id));
          break;
        case "edit":
          navigate(buildEditPath(item?.id));
          break;
        case "logs":
          setLogModalVisible(true);
          break;
      }
    },
    [navigate]
  );

  /**
   * Column definitions for the Specialities table.
   *
   * Returns:
   *   Array<object>: Column configuration for CustomTable.
   */
  const fields = useMemo(
    () => [
      { id: "name", label: "Speciality Name", flex: 2, sortable: true },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 2,
        sortable: true,
        render: (item: Speciality) => (
          <StatusBadge isActive={item.is_active ?? false} />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        type: "action",
        render: (item: Speciality) => (
          <CustomActionMenu<Speciality, SpecialityAction>
            item={item}
            isActive={item.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "View",
                  value: "view",
                  icon: <EyeIcon height={24} width={24} />,
                },
                ...(item.is_active
                  ? ([
                    {
                      label: "Edit",
                      value: "edit",
                      icon: <EditIcon height={24} width={24} />,
                    },
                  ] as ActionItem<SpecialityAction>[])
                  : []),
                {
                  label: "Logs",
                  value: "logs",
                  icon: <img src={Logs} alt="Logs" width={24} />,
                },
                {
                  label: item.is_active ? "Deactivate" : "Activate",
                  value: item.is_active ? "deactivate" : "activate",
                  icon: item.is_active ? (
                    <DeactivateIcon height={24} width={24} />
                  ) : (
                    <PowerSettingsNewIcon height={24} width={24} />
                  ),
                },
              ] as ActionItem<SpecialityAction>[]
            }
          />
        ),
      },
    ],
    [handleMenu]
  );

  /**
   * Updates the search term in query params.
   *
   * Args:
   *   e (ChangeEvent<HTMLInputElement>): Search input change event.
   *
   * Returns:
   *   void
   */
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: e.target.value,
      }));
    },
    [setQueryParam]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Submits the activation/deactivation update for the selected speciality.
   *
   * Args:
   *   values (FormState): Form values containing optional deactivate_reason.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Requires a selected speciality; falls back to id `0` if absent.
   * - TODO: Clarify desired behavior when no speciality is selected.
   */
  const onSubmit = async (values: FormState) => {
    try {
      if (!selectedSpeciality) {
        setMessage({
          type: "error",
          text: "Please select a speciality before updating status.",
        });
        return;
      }
      // Toggle active state based on the currently selected row; assumes selection is set via menu.
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateSpecialityDetails({
        is_active: !selectedSpeciality?.is_active,
        id: selectedSpeciality?.id ?? 0,
        deactivate_reason,
      }).unwrap();
      setIsLoading(false);
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
    } catch (error: unknown) {
      setIsLoading(false);
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    }
  };

  // Render the Speciality component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Speciality"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Speciality>
          fields={fields}
          paginationRequired={true}
          data={specialityData?.items || []}
          loading={isFetching}
          onDoubleClick={(speciality: Speciality) => {
            navigate(buildDetailsPath(speciality?.id));
          }}
          defaultOrderBy="name"
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedSpeciality?.is_active ? "deactivate " : "activate "
            }"${selectedSpeciality?.name}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            // Deactivation requires a reason; activation can proceed immediately without prompting.
            if (selectedSpeciality?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedSpeciality}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
}
export default Specialities;
